import { CommonModule } from '@angular/common';
import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  DateFnsAdapter,
  MAT_DATE_FNS_FORMATS,
} from '@angular/material-date-fns-adapter';
import { MatButtonModule } from '@angular/material/button';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatCalendar, MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { format, set } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  MAT_MENU_DEFAULT_OPTIONS,
  MatMenuModule,
  MatMenuTrigger,
} from '@angular/material/menu';

export interface IRangePreset {
  label?: string;
  from: string;
  to: string;
}
@Component({
  selector: 'app-datetime-range-picker',
  templateUrl: './component.html',
  styleUrls: ['./component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatButtonModule,
    MatIconModule,
    NgxMatTimepickerModule,
    MatMenuModule,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: es },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
    {
      provide: MAT_MENU_DEFAULT_OPTIONS,
      useValue: { overlayPanelClass: 'shadow' },
    },
  ],
})
export class DatetimeRangePickerComponent implements OnInit {
  @ViewChild(MatMenuTrigger) trigger?: MatMenuTrigger;
  @ViewChild('fromCalendar') fromCalendar?: MatCalendar<Date>;
  @ViewChild('toCalendar') toCalendar?: MatCalendar<Date>;

  @Input() range?: { from: string; to: string };
  @Output() rangeChange = new EventEmitter<{ from: string; to: string }>();

  @Input() presets: IRangePreset[] = [];

  public from?: Date;
  public to?: Date;
  public fromHour?: string;
  public toHour?: string;

  constructor() {}

  public cambioFrom(fechaFrom: Date) {
    if (this.fromHour) {
      const hours = +this.fromHour.slice(0, 2);
      const minutes = +this.fromHour.slice(3, 5);
      this.from = set(fechaFrom, { hours, minutes });
    } else {
      this.from = fechaFrom;
    }

    if (this.to && this.from > this.to) {
      this.to = undefined;
    }
    this.fromCalendar?.updateTodaysDate();
    this.toCalendar?.updateTodaysDate();
  }

  public cambioTo(fechaTo: Date) {
    if (this.toHour) {
      const hours = +this.toHour.slice(0, 2);
      const minutes = +this.toHour.slice(3, 5);
      this.to = set(fechaTo, { hours, minutes });
    } else {
      this.to = fechaTo;
    }
    this.fromCalendar?.updateTodaysDate();
    this.toCalendar?.updateTodaysDate();
  }

  public cambioHoraFrom(horaFrom: string) {
    if (!this.from) return;
    this.from = new Date(`${format(this.from, 'yyyy-MM-dd')} ${horaFrom}`);
    this.fromCalendar?.updateTodaysDate();
    this.toCalendar?.updateTodaysDate();
  }

  public cambioHoraTo(horaTo: string) {
    if (!this.to) return;
    this.to = new Date(`${format(this.to, 'yyyy-MM-dd')} ${horaTo}`);
    this.fromCalendar?.updateTodaysDate();
    this.toCalendar?.updateTodaysDate();
  }

  public cambioPreset(preset: IRangePreset) {
    this.from = new Date(preset.from);
    this.to = new Date(preset.to);
    this.toHour = this.getHour(this.to);
    this.fromHour = this.getHour(this.from);
    this.fromCalendar?.updateTodaysDate();
    this.toCalendar?.updateTodaysDate();
  }

  /**
   * Resalta los días dentro del rango. Es una referencia fija a propósito: antes
   * el template llamaba a una función que devolvía una closure nueva en cada
   * ciclo de detección, lo que para `mat-calendar` es un input que cambió y
   * re-renderizaba los 2 calendarios cada vez. El resaltado se refresca con
   * `updateTodaysDate()` cuando cambian `from`/`to`.
   */
  public readonly dateClass = (date: Date): string => {
    const highlightDate = date > this.from! && date < this.to!;
    return highlightDate ? 'special-date' : '';
  };

  //

  public aplicar() {
    const range = {
      from: this.from?.toISOString()!,
      to: this.to?.toISOString()!,
    };
    this.range = range;
    this.rangeChange.emit(range);
    this.trigger?.closeMenu();
  }

  public cancelar() {
    this.trigger?.closeMenu();
  }

  //
  private fechaHaceDias(dias: number, hora?: string): string {
    const fecha = new Date();
    fecha.setUTCHours(3, 0, 0, 0);

    if (hora) {
      const hour = +hora.slice(0, 2);
      const minutes = +hora.slice(3, 5);
      fecha.setHours(hour, minutes, 0, 0);
    }

    fecha.setDate(fecha.getDate() - dias);
    return fecha.toISOString();
  }

  private presetsIniciales() {
    const horaFrom = this.fromHour;
    const horaTo = this.toHour;

    this.presets = [
      {
        label: 'Últimas 24 horas',
        from: this.fechaHaceDias(1, horaFrom),
        to: this.fechaHaceDias(0, horaTo),
      },
      {
        label: 'Últimas 48 horas',
        from: this.fechaHaceDias(2, horaFrom),
        to: this.fechaHaceDias(0, horaTo),
      },
      {
        label: 'Últimos 7 días',
        from: this.fechaHaceDias(7, horaFrom),
        to: this.fechaHaceDias(0, horaTo),
      },
      {
        label: 'Últimos 30 días',
        from: this.fechaHaceDias(30, horaFrom),
        to: this.fechaHaceDias(0, horaTo),
      },
    ];
  }
  //

  private getHour(date: Date): string {
    const hour = `0${date.getHours()}`.slice(-2);
    const minutes = `0${date.getMinutes()}`.slice(-2);
    return `${hour}:${minutes}`;
  }

  ngOnInit() {
    if (this.range) {
      this.fromHour = this.range.from.slice(11, 16);
      this.toHour = this.range.to.slice(11, 16);
    }

    if (!this.presets.length) {
      this.presetsIniciales();
    }

    if (!this.range) {
      this.range = {
        from: this.fechaHaceDias(7),
        to: this.fechaHaceDias(0),
      };
    }

    this.from = new Date(this.range.from);
    this.fromHour = this.getHour(this.from);

    this.to = new Date(this.range.to);
    this.toHour = this.getHour(this.to);
  }
}
